import { supabase } from '../../supabase';
import { pkTodayKey } from '../../utils/pkTime';
import { cachedQuery } from '../cache/queryCache';
import { saleRowLeft } from '../../pages/receptionist/sales/salePaymentUtils';
import { parseSalePeriodFromNote } from '../../pages/receptionist/sales/salePeriodUtils';
import { durationFromSalePackage } from '../../pages/receptionist/sales/saleDurationUtils';
import { rowToPayment, rowToGuestPass } from '../receptionist/mappers';
import { attachMembershipSalesFromRows } from '../receptionist/membershipSalesAttach';

function isRpcMissing(error) {
  const msg = String(error?.message || error || '').toLowerCase();
  return (
    msg.includes('could not find the function')
    || msg.includes('function public.get_detailed_report_page')
    || msg.includes('schema cache')
    || (msg.includes('get_detailed_report_page') && msg.includes('does not exist'))
  );
}

function isoDate(value) {
  const d = String(value || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
}

export function mapDetailedReportPaymentRow(row) {
  const members = row.members || {};
  const payment = rowToPayment(row, {
    ...members,
    gym_member_id: members.gym_member_id,
    full_name: members.full_name,
    baseGymId: members.gym_member_id,
    trainer: members.trainer,
  });
  const fromNote = parseSalePeriodFromNote(row.payment_note || payment.paymentNote || '');
  const fromPackage = durationFromSalePackage(
    row.package_name || payment.package || payment.item || '',
  );
  const left = Number(row.pending_amount);
  return {
    ...payment,
    trainer: members.trainer || row.trainer || payment.trainer || '',
    branch: members.branch || row.branch_name || row.branch || '',
    duration: row.duration || payment.duration || fromNote?.durationLabel || fromPackage || '',
    startDate: row.start_date || payment.startDate || fromNote?.startDate || '',
    endDate: row.end_date || payment.endDate || fromNote?.endDate || '',
    pendingAmount: Number.isFinite(left) ? left : saleRowLeft(payment),
  };
}

export function mapDetailedReportExpenseRow(row) {
  const title = String(row.account_title || row.accountTitle || '').trim();
  const description = String(row.description || '').trim();
  const code = String(row.expense_code || row.expenseCode || row.code || '').trim();
  const accountCode = String(row.account_code || row.accountCode || '').trim();
  const rawMethod = String(row.payment_method || row.paymentMethod || row.method || '').trim().toLowerCase();
  const method = (rawMethod === 'bank' || rawMethod === 'card' || rawMethod === 'qr'
    || rawMethod === 'online' || rawMethod === 'transfer')
    ? 'bank'
    : 'cash';
  return {
    id: row.id,
    gymId: row.gym_id || '',
    accountTitle: title || description || 'Expense',
    description: title ? description : '',
    expenseCode: code || (/^E\d+/i.test(accountCode) ? accountCode.toUpperCase() : ''),
    code: code || (/^E\d+/i.test(accountCode) ? accountCode.toUpperCase() : ''),
    category: row.category || 'Other',
    branch: String(row.branch_name || row.branch || '').trim(),
    amount: Number(row.amount) || 0,
    date: row.expense_date || row.date || '',
    status: row.status || 'paid',
    accountCode: accountCode || '5000',
    paymentMethod: method,
    method,
  };
}

export async function fetchDetailedReportPageRpc({
  gymId,
  date,
  branch = '',
  receptionistScope = false,
} = {}) {
  if (!gymId) return null;
  const day = receptionistScope ? pkTodayKey() : isoDate(date);
  if (!day) return null;

  const branchValue = String(branch || '').trim();
  const pBranch = !branchValue || branchValue === 'all' ? null : branchValue;
  const cacheKey = `gym:${gymId}:detailed-report:${day}:${pBranch || 'all'}`;

  return cachedQuery(cacheKey, async () => {
    const { data, error } = await supabase.rpc('get_detailed_report_page', {
      p_gym_id: gymId,
      p_date: day,
      p_branch: pBranch,
      p_receptionist: Boolean(receptionistScope),
    });

    if (error) {
      if (isRpcMissing(error)) return null;
      throw error;
    }
    const payload = typeof data === 'string'
      ? (() => { try { return JSON.parse(data); } catch { return null; } })()
      : data;
    if (!payload || payload.error) return null;

    const payments = (payload.payments || []).map(mapDetailedReportPaymentRow);
    const membershipSales = attachMembershipSalesFromRows(
      day,
      payload.memberships || [],
      payload.member_payments || [],
      { applyReceptionistBranch: false },
    );
    const expenses = (payload.expenses || []).map(mapDetailedReportExpenseRow);
    const guestPasses = (payload.guest_passes || []).map((row) => ({
      ...rowToGuestPass(row),
      branch: row.branch_name || row.branch || '',
    }));

    return {
      date: day,
      totalPayments: Number(payload.total_payments) || 0,
      collected: Number(payload.collected) || 0,
      payments,
      membershipSales,
      expenses,
      guestPasses,
      fromRpc: true,
    };
  }, 45_000);
}
